"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ThemeProvider = ThemeProvider;
exports.getTheme = getTheme;
exports.getThemes = getThemes;
exports.isValidTheme = isValidTheme;
exports.onPreRenderHTML = void 0;
exports.setTheme = setTheme;
exports.useThemeHandler = useThemeHandler;
exports.wrapRootElement = wrapRootElement;
var _react = _interopRequireDefault(require("react"));
var _shared = require("@dnb/eufemia/shared");
var _inlineScriptProd = _interopRequireDefault(require("!raw-loader!terser-loader!./inlineScriptProd"));
var _inlineScriptDev = _interopRequireDefault(require("!raw-loader!terser-loader!./inlineScriptDev"));
var _EventEmitter = _interopRequireDefault(require("./EventEmitter.js"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
const defaultTheme = globalThis.EUFEMIA_THEME_defaultTheme || 'ui';
const availableThemes = globalThis.EUFEMIA_THEME_themes || [];
const storageId = globalThis.EUFEMIA_THEME_storageId || 'eufemia-theme';
const availableThemesArray = Object.keys(availableThemes);
function getThemes() {
  return availableThemes;
}
function isValidTheme(themeName) {
  return availableThemesArray.includes(themeName);
}
function useThemeHandler() {
  const [theme, setTheme] = _react.default.useState(getTheme);
  _react.default.useEffect(() => {
    const emitter = _EventEmitter.default.createInstance('themeHandler');
    emitter.listen(theme => {
      setTheme(theme);
    });
  }, []);
  return theme;
}
function getTheme() {
  if (typeof window === 'undefined') {
    return {
      name: defaultTheme
    };
  }
  try {
    const data = window.localStorage.getItem(storageId);
    const theme = JSON.parse(data?.startsWith('{') ? data : '{}');
    const regex = /.*eufemia-theme=([^&]*).*/;
    const query = window.location.search;
    const fromQuery = regex.test(query) && query?.replace(regex, '$1') || null;
    const themeName = fromQuery || theme?.name || defaultTheme;
    if (!isValidTheme(themeName)) {
      console.error('Not valid themeName:', themeName);
      return {
        name: defaultTheme
      }; // stop here
    }

    return {
      ...theme,
      name: themeName
    };
  } catch (e) {
    console.error(e);
    return {
      name: defaultTheme
    };
  }
}
function setTheme(themeProps, callback) {
  const theme = {
    ...getTheme(),
    ...themeProps
  };
  if (!isValidTheme(theme?.name)) {
    console.error('Not valid themeName:', theme?.name);
    return; // stop here
  }

  try {
    globalThis.__updateEufemiaThemeFile(theme.name, true, () => {
      const emitter = _EventEmitter.default.createInstance('themeHandler');
      emitter.update(theme);
      callback?.(theme);
    });
    window.localStorage.setItem(storageId, JSON.stringify(theme));
  } catch (e) {
    console.error(e);
  }
}
let cachedHeadComponents = [];
const onPreRenderHTML = ({
  getHeadComponents,
  replaceHeadComponents
}, pluginOptions) => {
  let headComponents = getHeadComponents();
  const isDev = process.env.NODE_ENV !== 'production';

  // Make themes to not be embedded, but rather load as css files
  if (!isDev) {
    let defaultElement;
    for (const element of headComponents) {
      const href = element.props['data-href'];
      if (href && href.includes('.css')) {
        if (availableThemesArray.some(name => {
          return href.includes(`/${name}.`);
        })) {
          const themeName = (href.match(/\/([^.]*)\./) || [])?.[1];

          // Collect all theme CSS file paths
          if (availableThemes[themeName]) {
            availableThemes[themeName].file = href;

            // Store the default inline styles,
            // and place it below data-href="/commons.*.css"
            if (pluginOptions?.inlineDefaultTheme && themeName === defaultTheme) {
              defaultElement = element;
              headComponents[element] = null;
            } else {
              // Remove the inline style
              // but not when its the default theme
              delete element.props['data-href'];
              delete element.props['data-identity'];
              delete element.props.dangerouslySetInnerHTML;
            }
          }
        }
      }
    }
    headComponents.push(defaultElement);
  } else {
    for (const key in availableThemes) {
      availableThemes[key].isDev = true;
      availableThemes[key].file = `/${key}.css`; // during dev, we get this file from the Webpack cache (not in /public)
    }
  }

  /**
   * We cache the result of the first page,
   * and re-use on all other pages.
   */
  if (cachedHeadComponents?.length) {
    headComponents.push(...cachedHeadComponents);
    replaceComponents(headComponents);
    return; // stop here
  }

  cachedHeadComponents = [];
  cachedHeadComponents.push( /*#__PURE__*/_react.default.createElement("link", {
    id: "eufemia-style-theme",
    key: "theme-style",
    rel: "stylesheet",
    type: "text/css",
    as: "style"
  }));
  const replaceGlobalVars = code => {
    return code.replace(/globalThis.isDev/g, JSON.stringify(isDev)).replace(/globalThis.defaultTheme/g, JSON.stringify(defaultTheme)).replace(/globalThis.storageId/g, JSON.stringify(storageId)).replace(/globalThis.availableThemes/g, JSON.stringify(availableThemes)).replace(/globalThis.inlineDefaultTheme/g, JSON.stringify(pluginOptions?.inlineDefaultTheme));
  };

  /**
   * NB: The dev script needs to be placed before the prod!
   */
  if (isDev) {
    cachedHeadComponents.push( /*#__PURE__*/_react.default.createElement("script", {
      key: "eufemia-style-theme-script-dev",
      dangerouslySetInnerHTML: {
        __html: replaceGlobalVars(_inlineScriptDev.default)
      }
    }));
  }
  cachedHeadComponents.push( /*#__PURE__*/_react.default.createElement("script", {
    key: "eufemia-style-theme-script-prod",
    dangerouslySetInnerHTML: {
      __html: replaceGlobalVars(_inlineScriptProd.default)
    }
  }));
  if (!pluginOptions?.inlineDefaultTheme) {
    cachedHeadComponents.push( /*#__PURE__*/_react.default.createElement("noscript", {
      key: "theme-style-fallback"
    }, /*#__PURE__*/_react.default.createElement("link", {
      id: "eufemia-style-theme-fallback",
      rel: "stylesheet",
      type: "text/css",
      as: "style",
      href: availableThemes[defaultTheme].file
    })));
  }
  headComponents.push(...cachedHeadComponents);
  replaceComponents(headComponents);
  function replaceComponents(headComponents) {
    const uniqueHeadComponents = [];

    // Remove duplications
    headComponents.forEach(item => {
      const exists = uniqueHeadComponents.some(i => {
        const href = i?.props?.['data-href'];
        return href && item?.props?.['data-href'] === href;
      });
      if (!exists) {
        uniqueHeadComponents.push(item);
      }
    });
    replaceHeadComponents(uniqueHeadComponents);
  }
};
exports.onPreRenderHTML = onPreRenderHTML;
function ThemeProvider({
  children
}) {
  const theme = useThemeHandler();
  return /*#__PURE__*/_react.default.createElement(_shared.Theme, theme, children);
}
function wrapRootElement({
  element
}, pluginOptions) {
  if (pluginOptions?.wrapWithThemeProvider) {
    return /*#__PURE__*/_react.default.createElement(ThemeProvider, null, element);
  }
  return element;
}